import { AsyncPipe, NgIf } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import type { PaletteType, SizeType } from '@cf/core';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  InputPrefixDirective,
  InputSuffixDirective,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { TextComponent } from '@cf/shared/ui/typography';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { PasswordStrengthMeterComponent } from 'angular-password-strength-meter';
import { NgxSvgSpriteFragment } from 'ngxtension/svg-sprite';

@Component({
  selector: 'cf-password-input',
  templateUrl: './password-input.component.html',
  styleUrls: ['./password-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PasswordInputComponent),
    },
  ],
  imports: [
    NgIf,
    FormFieldComponent,
    InputDirective,
    InputPrefixDirective,
    InputSuffixDirective,
    InputErrorComponent,
    LabelComponent,
    FormsModule,
    ReactiveFormsModule,
    CfIconDirective,
    PasswordStrengthMeterComponent,
    TextComponent,
    NgxTolgeeModule,
    NgxSvgSpriteFragment,
    AsyncPipe,
  ],
  standalone: true,
})
export class PasswordInputComponent implements ControlValueAccessor {
  translateService = inject(TranslateService);
  private formBuilder = inject(FormBuilder);

  @Input() label = '';

  @Input() labelSize: SizeType = 'sm';

  @Input() showPasswordStrength = false;

  @Output() emitChanges = new EventEmitter();

  public password: FormGroup;

  public showPassword = false;

  public passwordStrength = 0;

  public passwordColor: PaletteType = 'red-60';

  public passwordStrengthName = this.translateService.translate(
    'forms.password_input.strength.low',
  );

  private destroyRef = inject(DestroyRef);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-function
  onChange = (_: any) => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: never): void {
    this.onTouched = fn;
  }

  writeValue(obj: any) {
    if (obj) {
      this.password.patchValue({ password: obj });
    }
  }

  constructor() {
    this.password = this.formBuilder.group({
      password: new FormControl('', [Validators.required]),
    });

    this.password.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.onChange(value.password);
      this.onTouched();
    });
  }

  get passwordValue() {
    return this.password.value.password;
  }

  get invalid() {
    return this.passwordStrength < 3;
  }

  onStrengthChanged(strength: number | null) {
    this.passwordStrength = strength as number;
    if (this.passwordStrength < 2) {
      this.passwordColor = 'red-60';
      this.passwordStrengthName = this.translateService.translate(
        'forms.password_input.strength.low',
      );
    } else if (this.passwordStrength < 3) {
      this.passwordColor = 'yellow-50';
      this.passwordStrengthName = this.translateService.translate(
        'forms.password_input.strength.medium',
      );
    } else if (this.passwordStrength < 4) {
      this.passwordColor = 'green-40';
      this.passwordStrengthName = this.translateService.translate(
        'forms.password_input.strength.good',
      );
    } else {
      this.passwordColor = 'green-70';
      this.passwordStrengthName = this.translateService.translate(
        'forms.password_input.strength.perfect',
      );
    }
    this.emitChanges.emit();
  }
}
